import { useCallback, useMemo, useState } from 'react';

import { BarChart3 } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import type { Customer, JournalEntry, TimeEntry } from '@/pages/developer/types';

const DEFAULT_HOURLY_RATE = 65;

type SnapshotTarget =
  | { kind: 'developer' }
  | { kind: 'customer'; customerId: string };

const parseSnapshotTarget = (value: string): SnapshotTarget => {
  if (value === 'developer') return { kind: 'developer' };
  if (value.startsWith('customer:')) {
    const customerId = value.slice('customer:'.length).trim();
    if (customerId) return { kind: 'customer', customerId };
  }
  return { kind: 'developer' };
};

const snapshotTargetToValue = (target: SnapshotTarget) => {
  if (target.kind === 'developer') return 'developer';
  return `customer:${target.customerId}`;
};

const safeNumber = (value: unknown) => {
  const numberValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

export type FinancialSnapshotCardProps = {
  customers: Customer[];
  journalEntries: JournalEntry[];
  timeEntries: TimeEntry[];
  defaultCommitRate: number;
};

export function FinancialSnapshotCard({ customers, journalEntries, timeEntries, defaultCommitRate }: FinancialSnapshotCardProps) {
  const [target, setTarget] = useState<SnapshotTarget>({ kind: 'developer' });

  const customerById = useMemo(() => {
    const map = new Map<string, Customer>();
    customers.forEach((customer) => map.set(customer.id, customer));
    return map;
  }, [customers]);

  const gitOrders = useMemo(() => {
    return timeEntries.filter((entry) => entry.kind === 'git-order' || (!entry.kind && (entry.commitShas?.length || entry.commitRate || entry.misc || entry.taxes || entry.invoiceJournalEntryId)));
  }, [timeEntries]);

  const timeTickets = useMemo(() => {
    return timeEntries.filter((entry) => entry.kind === 'time-ticket' || (!entry.kind && !(entry.commitShas?.length || entry.commitRate || entry.misc || entry.taxes || entry.invoiceJournalEntryId)));
  }, [timeEntries]);

  const computeGitOrderTotal = useCallback((entry: TimeEntry) => {
    const qty = (entry.commitShas ?? []).length;
    const rate = typeof entry.commitRate === 'number' ? entry.commitRate : defaultCommitRate;
    const misc = typeof entry.misc === 'number' ? entry.misc : 0;
    const taxes = typeof entry.taxes === 'number' ? entry.taxes : 0;
    return qty * rate + misc + taxes;
  }, [defaultCommitRate]);

  const developerTotals = useMemo(() => {
    const invoiceSum = journalEntries.reduce((acc, entry) => {
      const amount = safeNumber(entry.amount);
      if (amount >= 0) acc.revenue += amount;
      else acc.costs += Math.abs(amount);
      acc.net += amount;
      return acc;
    }, { revenue: 0, costs: 0, net: 0 });

    const invested = timeTickets.reduce((sum, entry) => {
      const hours = safeNumber(entry.hours);
      const rate = typeof entry.hourlyRate === 'number' ? entry.hourlyRate : DEFAULT_HOURLY_RATE;
      return sum + hours * rate;
    }, 0);

    const openGitOrders = gitOrders
      .filter((entry) => (entry.invoiceJournalEntryId ?? '').trim().length === 0 && entry.stage !== 'invoiced')
      .reduce((sum, entry) => sum + computeGitOrderTotal(entry), 0);

    const closedGitOrders = gitOrders
      .filter((entry) => (entry.invoiceJournalEntryId ?? '').trim().length > 0 || entry.stage === 'invoiced')
      .reduce((sum, entry) => sum + computeGitOrderTotal(entry), 0);

    return {
      revenue: invoiceSum.revenue,
      costs: invoiceSum.costs,
      net: invoiceSum.net,
      invested,
      openGitOrders,
      closedGitOrders,
    };
  }, [computeGitOrderTotal, gitOrders, journalEntries, timeTickets]);

  const customerTotals = useMemo(() => {
    if (target.kind !== 'customer') return null;
    const customer = customerById.get(target.customerId);
    const customerName = (customer?.name ?? '').trim().toLowerCase();

    const customerInvoices = journalEntries.filter((entry) => {
      const entryCustomerId = (entry.customerId ?? '').trim();
      if (entryCustomerId) return entryCustomerId === target.customerId;
      if (!customerName) return false;
      return (entry.customer ?? '').trim().toLowerCase() === customerName;
    });

    const invoiceIds = new Set(customerInvoices.map((entry) => entry.id));

    const invoiceTotal = customerInvoices.reduce((sum, entry) => sum + safeNumber(entry.amount), 0);

    const linkedGitOrders = gitOrders.filter((entry) => {
      const invoiceId = (entry.invoiceJournalEntryId ?? '').trim();
      return invoiceId && invoiceIds.has(invoiceId);
    });

    const linkedGitOrderTotal = linkedGitOrders.reduce((sum, entry) => sum + computeGitOrderTotal(entry), 0);

    return {
      invoiceTotal,
      invoiceCount: customerInvoices.length,
      linkedGitOrderCount: linkedGitOrders.length,
      linkedGitOrderTotal,
    };
  }, [computeGitOrderTotal, customerById, gitOrders, journalEntries, target]);

  const dropdownItems = useMemo(() => {
    const customerItems = customers
      .filter(customer => customer && customer.id)
      .slice()
      .sort((a, b) => (a?.name ?? '').localeCompare(b?.name ?? ''))
      .map((customer) => ({ value: `customer:${customer.id}`, label: (customer?.name ?? customer.id) }));

    return [{ value: 'developer', label: 'Developer snapshot' }, ...customerItems];
  }, [customers]);

  const selectedValue = snapshotTargetToValue(target);

  return (
    <Card className="mb-8">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Financial snapshot
        </CardTitle>
        <CardDescription>Quick totals across invoices, Git orders, and invested (unclaimed) time.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="w-full sm:max-w-[320px]">
            <Select
              value={selectedValue}
              onValueChange={(value) => {
                setTarget(parseSnapshotTarget(value));
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select snapshot" />
              </SelectTrigger>
              <SelectContent>
                {dropdownItems.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {target.kind === 'developer' ? (
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="rounded-lg border border-border/60 bg-muted/10 p-4">
              <p className="text-xs text-muted-foreground">Invoice revenue</p>
              <p className="mt-1 text-xl font-semibold text-foreground">${developerTotals.revenue.toFixed(2)}</p>
            </div>
            <div className="rounded-lg border border-border/60 bg-muted/10 p-4">
              <p className="text-xs text-muted-foreground">Invoice costs</p>
              <p className="mt-1 text-xl font-semibold text-foreground">${developerTotals.costs.toFixed(2)}</p>
            </div>
            <div className="rounded-lg border border-border/60 bg-muted/10 p-4">
              <p className="text-xs text-muted-foreground">Invoice net</p>
              <p className="mt-1 text-xl font-semibold text-foreground">${developerTotals.net.toFixed(2)}</p>
            </div>

            <div className="rounded-lg border border-border/60 bg-muted/10 p-4">
              <p className="text-xs text-muted-foreground">Open Git orders (draft)</p>
              <p className="mt-1 text-xl font-semibold text-foreground">${developerTotals.openGitOrders.toFixed(2)}</p>
            </div>
            <div className="rounded-lg border border-border/60 bg-muted/10 p-4">
              <p className="text-xs text-muted-foreground">Closed Git orders (value)</p>
              <p className="mt-1 text-xl font-semibold text-foreground">${developerTotals.closedGitOrders.toFixed(2)}</p>
            </div>
            <div className="rounded-lg border border-border/60 bg-muted/10 p-4">
              <p className="text-xs text-muted-foreground">Invested time (unclaimed)</p>
              <p className="mt-1 text-xl font-semibold text-foreground">${developerTotals.invested.toFixed(2)}</p>
              <p className="mt-1 text-xs text-muted-foreground">Defaults to $65/hr when rate missing.</p>
            </div>
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="rounded-lg border border-border/60 bg-muted/10 p-4">
              <p className="text-xs text-muted-foreground">Invoices total</p>
              <p className="mt-1 text-xl font-semibold text-foreground">${(customerTotals?.invoiceTotal ?? 0).toFixed(2)}</p>
              <p className="mt-1 text-xs text-muted-foreground">{customerTotals?.invoiceCount ?? 0} invoice entries</p>
            </div>
            <div className="rounded-lg border border-border/60 bg-muted/10 p-4">
              <p className="text-xs text-muted-foreground">Linked Git orders</p>
              <p className="mt-1 text-xl font-semibold text-foreground">{customerTotals?.linkedGitOrderCount ?? 0}</p>
            </div>
            <div className="rounded-lg border border-border/60 bg-muted/10 p-4">
              <p className="text-xs text-muted-foreground">Linked Git orders (value)</p>
              <p className="mt-1 text-xl font-semibold text-foreground">${(customerTotals?.linkedGitOrderTotal ?? 0).toFixed(2)}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
